import React, { useEffect, useState, useRef } from 'react';
import { treatmentApi, inventoryApi, farmApi, boxApi, medicineApi } from '../services/api';
import { Treatment, Medicine, MedicineInventory, Box, Farm } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import AnimalSelectorModal, { SelectedAnimal } from '../components/AnimalSelectorModal';
import ClearableSelect from '../components/ClearableSelect';
import Pagination from '../components/Pagination';

const Treatments: React.FC = () => {
  const { t } = useLanguage();
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'treatment' | 'vaccination'>('treatment');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAnimalSelector, setShowAnimalSelector] = useState(false);
  const [animalDisplay, setAnimalDisplay] = useState('');
  const [allMedicines, setAllMedicines] = useState<Medicine[]>([]);
  const [medicineInventory, setMedicineInventory] = useState<MedicineInventory[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectedFarmId, setSelectedFarmId] = useState<number>(0);
  const [selectedStableId, setSelectedStableId] = useState<number>(0);
  const [selectedBoxIds, setSelectedBoxIds] = useState<number[]>([]);
  const [selectedMedicineInventoryId, setSelectedMedicineInventoryId] = useState<number>(0);
  const [formState, setFormState] = useState({
    treatment_level: 'animal' as 'animal' | 'box',
    treatment_type: 'treatment' as 'treatment' | 'vaccination',
    animal_id: 0,
    treatment_date: new Date().toISOString().split('T')[0],
    dosage: 0,
    withdrawal_days: 0,
    notes: '',
    veterinary_approved: false,
  });

  // Filters
  const [filterMedicineId, setFilterMedicineId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  useEffect(() => {
    loadAllMedicines();
    loadFarms();
  }, []);

  useEffect(() => {
    if (selectedFarmId > 0) {
      loadBoxes(selectedFarmId);
      loadMedicineInventory(selectedFarmId);
    } else {
      setBoxes([]);
      setMedicineInventory([]);
    }
  }, [selectedFarmId]);

  const prevDeps = useRef({ activeTab, filterMedicineId, filterDateFrom, filterDateTo });
  const isInitialMount = useRef(true);

  useEffect(() => {
    const depsChanged = !isInitialMount.current && (
      prevDeps.current.activeTab !== activeTab ||
      prevDeps.current.filterMedicineId !== filterMedicineId ||
      prevDeps.current.filterDateFrom !== filterDateFrom ||
      prevDeps.current.filterDateTo !== filterDateTo
    );
    prevDeps.current = { activeTab, filterMedicineId, filterDateFrom, filterDateTo };
    isInitialMount.current = false;

    if (depsChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    loadTreatments(currentPage);
  }, [currentPage, activeTab, filterMedicineId, filterDateFrom, filterDateTo]);

  const loadTreatments = async (page: number) => {
    try {
      setLoading(true);
      const params: Record<string, any> = { page, treatment_type: activeTab };
      if (filterMedicineId) params.medicine_id = filterMedicineId;
      if (filterDateFrom) params.from_date = filterDateFrom;
      if (filterDateTo) params.to_date = filterDateTo;
      const response = await treatmentApi.getAll(params);
      setTreatments(response.data);
      setCurrentPage(response.current_page);
      setLastPage(response.last_page);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading treatments:', error);
      handleApiError(error, 'Hiba a kezelések betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleAnimalSelected = (animals: SelectedAnimal[]) => {
    const animal = animals[0];
    setFormState(prev => ({ ...prev, animal_id: animal.id }));
    setAnimalDisplay(`${animal.ear_tag}${animal.breed ? ` - ${animal.breed}` : ''}`);
    setShowAnimalSelector(false);
  };

  const loadAllMedicines = async () => {
    try {
      // Load all medicines for filter dropdown (unfiltered)
      const data = await medicineApi.getAll();
      setAllMedicines(data);
    } catch (error) {
      console.error('Error loading medicines:', error);
      handleApiError(error, 'Hiba a gyógyszerek betöltésekor');
    }
  };

  const loadMedicineInventory = async (farmId: number) => {
    try {
      const response = await inventoryApi.getMedicines(farmId, true);
      setMedicineInventory(response.data || response);
    } catch (error) {
      console.error('Error loading medicine inventory:', error);
      setMedicineInventory([]);
    }
  };

  const loadFarms = async () => {
    try {
      const data = await farmApi.getAll();
      setFarms(data);
    } catch (error) {
      console.error('Error loading farms:', error);
      handleApiError(error, 'Hiba a telephelyek betöltésekor');
    }
  };

  const loadBoxes = async (farmId: number) => {
    try {
      const data = await boxApi.getAll(farmId);
      setBoxes(data);
    } catch (error) {
      console.error('Error loading boxes:', error);
      handleApiError(error, 'Hiba a boxok betöltésekor');
      setBoxes([]);
    }
  };

  // Get selected inventory record
  const selectedInventory = medicineInventory.find(inv => inv.id === selectedMedicineInventoryId);

  // Compute stock needed and whether sufficient
  const totalAnimalsInSelectedBoxes = formState.treatment_level === 'box'
    ? selectedBoxIds.reduce((sum, boxId) => {
        const box = boxes.find(b => b.id === boxId);
        return sum + (box?.current_animal_count ?? box?.animals_count ?? 0);
      }, 0)
    : 1;
  const stockNeeded = formState.dosage * totalAnimalsInSelectedBoxes;
  const stockAvailable = selectedInventory?.quantity ?? 0;
  const stockSufficient = !selectedMedicineInventoryId || stockNeeded <= stockAvailable;

  const handleAddTreatment = async () => {
    try {
      // Validation
      if (!selectedFarmId) {
        showError(t('select_farm', 'treatments') || 'Válassz gazdaságot!');
        return;
      }
      if (!selectedMedicineInventoryId) {
        showError(t('select_medicine', 'treatments') || 'Válassz gyógyszert!');
        return;
      }
      if (formState.treatment_level === 'box') {
        if (selectedBoxIds.length === 0) {
          showError(t('select_box_and_medicine', 'treatments') || 'Válassz legalább egy boxot!');
          return;
        }
      } else {
        if (!formState.animal_id) {
          showError(t('select_animal_and_medicine', 'treatments') || 'Válassz állatot!');
          return;
        }
      }
      if (!formState.dosage || formState.dosage <= 0) {
        showError(t('enter_dosage', 'treatments') || 'Add meg az adagot!');
        return;
      }
      if (!stockSufficient) {
        showError(`Nincs elegendő készlet. Elérhető: ${stockAvailable} ${selectedInventory?.quantity_unit}, Szükséges: ${stockNeeded} ${selectedInventory?.quantity_unit}`);
        return;
      }

      setSubmitting(true);

      if (formState.treatment_level === 'box') {
        const requestData = {
          treatment_type: formState.treatment_type,
          medicine_inventory_id: selectedMedicineInventoryId,
          box_ids: selectedBoxIds,
          treatment_date: formState.treatment_date,
          dosage: formState.dosage,
          withdrawal_days: formState.withdrawal_days,
          notes: formState.notes || undefined,
        };

        await treatmentApi.boxBulkTreatment(requestData);
      } else {
        await treatmentApi.create({
          treatment_level: 'animal',
          treatment_type: formState.treatment_type,
          animal_id: formState.animal_id,
          medicine_inventory_id: selectedMedicineInventoryId,
          treatment_date: formState.treatment_date,
          dosage: formState.dosage,
          withdrawal_days: formState.withdrawal_days,
          notes: formState.notes || undefined,
          veterinary_approved: formState.veterinary_approved,
        } as any);
      }

      setShowAddModal(false);
      resetForm();
      loadTreatments(currentPage);
      showSuccess('Kezelés sikeresen rögzítve');
    } catch (error: any) {
      console.error('Error creating treatment:', error);
      handleApiError(error, 'Hiba a kezelés létrehozásakor');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormState({
      treatment_level: 'animal',
      treatment_type: 'treatment',
      animal_id: 0,
      treatment_date: new Date().toISOString().split('T')[0],
      dosage: 0,
      withdrawal_days: 0,
      notes: '',
      veterinary_approved: false,
    });
    setAnimalDisplay('');
    setSelectedFarmId(0);
    setSelectedStableId(0);
    setSelectedBoxIds([]);
    setSelectedMedicineInventoryId(0);
    setMedicineInventory([]);
    setBoxes([]);
  };

  const handleMedicineInventoryChange = (inventoryId: number) => {
    setSelectedMedicineInventoryId(inventoryId);
    const inv = medicineInventory.find(i => i.id === inventoryId);
    if (inv?.medicine) {
      setFormState(prev => ({
        ...prev,
        withdrawal_days: inv.medicine?.default_withdrawal_days || 0,
      }));
    }
  };

  const handleFarmChange = (farmId: number) => {
    setSelectedFarmId(farmId);
    // Reset downstream selections
    setSelectedStableId(0);
    setSelectedBoxIds([]);
    setSelectedMedicineInventoryId(0);
    setFormState(prev => ({ ...prev, animal_id: 0 }));
    setAnimalDisplay('');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  // Get unique stables from loaded boxes
  const getStables = () => {
    const stableMap = new Map();
    boxes.forEach(box => {
      if (box.stable && !stableMap.has(box.stable.id)) {
        stableMap.set(box.stable.id, box.stable);
      }
    });
    return Array.from(stableMap.values());
  };

  // Get boxes filtered by stable
  const getFilteredBoxes = () => {
    if (selectedStableId) {
      return boxes.filter(box => box.stable_id === selectedStableId);
    }
    return boxes;
  };

  // Select all boxes in a specific stable
  const selectAllBoxesInStable = (stableId: number) => {
    const stableBoxes = boxes.filter(box => box.stable_id === stableId);
    const stableBoxIds = stableBoxes.map(box => box.id);
    const allSelected = stableBoxIds.every(id => selectedBoxIds.includes(id));

    if (allSelected) {
      setSelectedBoxIds(prev => prev.filter(id => !stableBoxIds.includes(id)));
    } else {
      setSelectedBoxIds(prev => {
        const newIds = [...prev];
        stableBoxIds.forEach(id => {
          if (!newIds.includes(id)) newIds.push(id);
        });
        return newIds;
      });
    }
  };

  const handleAddClick = () => {
    setFormState(prev => ({ ...prev, treatment_type: activeTab }));
    setShowAddModal(true);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-128px)]">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-3xl font-bold text-gray-900">{t('treatments_management', 'treatments')}</h1>
        <p className="mt-2 text-gray-600">
          {t('treatments_management_desc', 'treatments')}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex-shrink-0">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('treatment')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'treatment'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{t('treatment', 'treatments')}</span>
                {activeTab === 'treatment' && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {total}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('vaccination')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'vaccination'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('vaccination', 'treatments')}</span>
                {activeTab === 'vaccination' && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {total}
                  </span>
                )}
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-4 flex-shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('medicine', 'treatments')}
            </label>
            <ClearableSelect
              value={filterMedicineId}
              onChange={(val) => setFilterMedicineId(val)}
              placeholder={t('all_medicines', 'treatments') || 'All medicines'}
              disabled={loading}
            >
              {allMedicines.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </ClearableSelect>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('date_from', 'common') || 'Date From'}
            </label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('date_to', 'common') || 'Date To'}
            </label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg flex flex-col flex-1 min-h-0">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {activeTab === 'treatment' ? t('treatment', 'treatments') : t('vaccination', 'treatments')} ({total})
          </h3>
          <button
            onClick={handleAddClick}
            className={`text-white px-4 py-2 rounded-md flex items-center space-x-2 ${
              activeTab === 'treatment'
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>{t('add', 'action')}</span>
          </button>
        </div>

        <div className="overflow-auto flex-1 min-h-0 relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-60 z-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-[5]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('target', 'treatments')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('type', 'treatments')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('medicine', 'treatments')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dosage', 'treatments')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('date', 'treatments')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('withdrawal_time', 'treatments')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {treatments.map((treatment) => (
                <tr key={treatment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {treatment.treatment_level === 'box'
                        ? treatment.box?.name || `Box ID: ${treatment.box_id}`
                        : treatment.animal?.ear_tag || `Animal ID: ${treatment.animal_id}`
                      }
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        treatment.treatment_level === 'box'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {treatment.treatment_level === 'box' ? t('box_treatment', 'treatments') : t('individual_treatment', 'treatments')}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        (treatment as any).treatment_type === 'vaccination'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {(treatment as any).treatment_type === 'vaccination' ? t('vaccination', 'treatments') : t('treatment', 'treatments')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {treatment.medicine?.name || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {treatment.dosage} {treatment.dosage_unit}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(treatment.treatment_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {treatment.withdrawal_days} {t('days', 'treatments')}
                    </div>
                    {treatment.withdrawal_end_date && (
                      <div className="text-xs text-gray-500">
                        {t('end', 'treatments')}: {formatDate(treatment.withdrawal_end_date)}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {treatments.length === 0 && !loading && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {activeTab === 'treatment' ? t('no_treatments', 'treatments') : t('no_vaccinations', 'treatments')}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeTab === 'treatment' ? t('treatments_will_appear_here', 'treatments') : t('vaccinations_will_appear_here', 'treatments')}
              </p>
            </div>
          )}
        </div>

        {lastPage > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 flex-shrink-0">
            <div className="text-sm text-gray-700">
              {((currentPage - 1) * 50) + 1}–{Math.min(currentPage * 50, total)} / {total}
            </div>
            <Pagination page={currentPage} lastPage={lastPage} onPageChange={setCurrentPage} disabled={loading} />
          </div>
        )}
      </div>

      {/* Add Treatment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white mb-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{t('add_new_treatment', 'treatments')}</h3>
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* 1. Farm Selector (gates everything) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('farm', 'treatments') || 'Gazdaság'} *
                </label>
                <select
                  value={selectedFarmId || ''}
                  onChange={(e) => handleFarmChange(e.target.value ? parseInt(e.target.value) : 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">{t('select_farm', 'treatments') || 'Válassz gazdaságot'}...</option>
                  {farms.map((farm) => (
                    <option key={farm.id} value={farm.id}>{farm.name}</option>
                  ))}
                </select>
              </div>

              {/* 2. Treatment Level Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('treatment_level', 'treatments')} *
                </label>
                <select
                  value={formState.treatment_level}
                  onChange={(e) => {
                    const level = e.target.value as 'animal' | 'box';
                    setFormState(prev => ({ ...prev, treatment_level: level, animal_id: 0 }));
                    setAnimalDisplay('');
                    setSelectedBoxIds([]);
                    setSelectedStableId(0);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={!selectedFarmId}
                  required
                >
                  <option value="animal">{t('individual_treatment', 'treatments')}</option>
                  <option value="box">{t('box_treatment', 'treatments')}</option>
                </select>
              </div>

              {/* 3. Treatment Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('treatment_type', 'treatments')} *
                </label>
                <select
                  value={formState.treatment_type}
                  onChange={(e) => setFormState(prev => ({
                    ...prev,
                    treatment_type: e.target.value as 'treatment' | 'vaccination'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={!selectedFarmId}
                  required
                >
                  <option value="treatment">{t('treatment', 'treatments')}</option>
                  <option value="vaccination">{t('vaccination', 'treatments')}</option>
                </select>
              </div>

              {/* 4a. Animal Selection (individual) */}
              {formState.treatment_level === 'animal' && selectedFarmId > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('animal', 'treatments')} *
                  </label>
                  <div className="relative">
                    <div
                      onClick={() => setShowAnimalSelector(true)}
                      className={`w-full px-3 py-2 border rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        formState.animal_id ? 'border-gray-300 text-gray-900' : 'border-gray-300 text-gray-400'
                      }`}
                    >
                      {animalDisplay || `${t('select_animal', 'treatments') || 'Állat kiválasztása'}...`}
                    </div>
                    {formState.animal_id ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormState(prev => ({ ...prev, animal_id: 0 }));
                          setAnimalDisplay('');
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                </div>
              )}

              {/* 4b. Stable Filter + Box Selection (box treatment) */}
              {formState.treatment_level === 'box' && selectedFarmId > 0 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('stable', 'treatments') || 'Istálló'} ({t('filter', 'treatments') || 'szűrő'})
                    </label>
                    <select
                      value={selectedStableId || ''}
                      onChange={(e) => setSelectedStableId(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">{t('all_stables', 'treatments') || 'Összes istálló'}</option>
                      {getStables().map((stable) => (
                        <option key={stable.id} value={stable.id}>{stable.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('box', 'treatments') || 'Box'} * ({selectedBoxIds.length} {t('selected', 'treatments') || 'kiválasztva'})
                    </label>
                    <div className="border border-gray-300 rounded-md max-h-96 overflow-y-auto p-2">
                      {boxes.length === 0 ? (
                        <p className="text-gray-500 text-sm p-2">{t('no_boxes_available', 'treatments') || 'Nincsenek elérhető boxok'}</p>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center p-2 border-b border-gray-200">
                            <input
                              type="checkbox"
                              id="select-all-boxes"
                              checked={selectedBoxIds.length === getFilteredBoxes().length && getFilteredBoxes().length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedBoxIds(getFilteredBoxes().map(b => b.id));
                                } else {
                                  setSelectedBoxIds([]);
                                }
                              }}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label htmlFor="select-all-boxes" className="ml-2 text-sm font-medium text-gray-900">
                              {selectedBoxIds.length === getFilteredBoxes().length && getFilteredBoxes().length > 0
                                ? (t('deselect_all', 'treatments') || 'Mind törlése')
                                : (t('select_all', 'treatments') || 'Mind kiválasztása')}
                            </label>
                          </div>

                          {getStables()
                            .filter(stable => !selectedStableId || stable.id === selectedStableId)
                            .map((stable) => {
                              const stableBoxes = boxes.filter(box => box.stable_id === stable.id);
                              const stableBoxIds = stableBoxes.map(box => box.id);
                              const allStableBoxesSelected = stableBoxIds.length > 0 && stableBoxIds.every(id => selectedBoxIds.includes(id));

                              return (
                                <div key={stable.id} className="border border-gray-200 rounded-md p-2">
                                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-t-md border-b border-gray-200 mb-2">
                                    <span className="text-sm font-semibold text-gray-700">{stable.name}</span>
                                    <button
                                      type="button"
                                      onClick={() => selectAllBoxesInStable(stable.id)}
                                      className={`text-xs px-3 py-1 rounded ${
                                        allStableBoxesSelected
                                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                      }`}
                                    >
                                      {allStableBoxesSelected
                                        ? (t('deselect_all', 'treatments') || 'Mind törlése')
                                        : (t('select_all', 'treatments') || 'Mind kiválasztása')}
                                    </button>
                                  </div>

                                  <div className="space-y-1">
                                    {stableBoxes.map((box) => (
                                      <div key={box.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                                        <input
                                          type="checkbox"
                                          id={`box-${box.id}`}
                                          checked={selectedBoxIds.includes(box.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setSelectedBoxIds(prev => [...prev, box.id]);
                                            } else {
                                              setSelectedBoxIds(prev => prev.filter(id => id !== box.id));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor={`box-${box.id}`} className="ml-2 text-sm text-gray-900 flex-1">
                                          {box.name} <span className="text-gray-500">({box.current_animal_count || box.animals_count || 0} {t('animals', 'treatments') || 'állat'})</span>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* 5. Medicine Inventory Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('medicine', 'treatments') || 'Gyógyszer'} *
                </label>
                <select
                  value={selectedMedicineInventoryId || ''}
                  onChange={(e) => handleMedicineInventoryChange(e.target.value ? parseInt(e.target.value) : 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={!selectedFarmId}
                  required
                >
                  <option value="">{t('select_medicine', 'treatments') || 'Válassz gyógyszert'}...</option>
                  {medicineInventory.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.display_name} — {inv.quantity} {t('available', 'common') || 'elérhető'}
                    </option>
                  ))}
                </select>
                {selectedFarmId > 0 && medicineInventory.length === 0 && (
                  <p className="mt-1 text-sm text-amber-600">
                    Nincs elérhető gyógyszer ezen a gazdaságon. Először vegyél fel készletet a Készlet oldalon.
                  </p>
                )}
              </div>

              {/* 6. Dosage + unit (read-only) + available stock */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dosage', 'treatments') || 'Adag'}{formState.treatment_level === 'box' ? ' (állatonként)' : ''} *
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      value={formState.dosage || ''}
                      onChange={(e) => setFormState(prev => ({ ...prev, dosage: parseFloat(e.target.value) || 0 }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder={t('quantity', 'treatments') || 'Mennyiség'}
                      min="0"
                      step="0.1"
                      disabled={!selectedMedicineInventoryId}
                      required
                    />
                    <span className="inline-flex items-center px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 font-medium">
                      {selectedInventory?.quantity_unit || '—'}
                    </span>
                  </div>
                  {selectedInventory && (
                    <div className={`mt-1 text-sm ${stockSufficient ? 'text-gray-500' : 'text-red-600 font-medium'}`}>
                      {t('available', 'common') || 'Elérhető'}: {stockAvailable} {selectedInventory.quantity_unit}
                      {formState.treatment_level === 'box' && totalAnimalsInSelectedBoxes > 0 && formState.dosage > 0 && (
                        <span> | {totalAnimalsInSelectedBoxes} állat × {formState.dosage} = {stockNeeded} {selectedInventory.quantity_unit}</span>
                      )}
                      {!stockSufficient && (
                        <span className="block text-red-600">
                          Nincs elegendő készlet!
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Treatment Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('treatment_date', 'treatments') || 'Kezelés dátuma'} *
                  </label>
                  <input
                    type="date"
                    value={formState.treatment_date}
                    onChange={(e) => setFormState(prev => ({ ...prev, treatment_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              {/* 7. Withdrawal Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('withdrawal_time_days', 'treatments') || 'Várakozási idő (nap)'} *
                </label>
                {(() => {
                  const selectedInv = medicineInventory.find(i => i.id === selectedMedicineInventoryId);
                  const defaultDays = selectedInv?.medicine?.default_withdrawal_days || 0;
                  const minDays = defaultDays > 0 ? defaultDays : 0;
                  const isInvalid = minDays > 0 && formState.withdrawal_days < minDays;
                  return (
                    <>
                      <input
                        type="number"
                        value={formState.withdrawal_days || ''}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setFormState(prev => ({ ...prev, withdrawal_days: val }));
                        }}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          isInvalid
                            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
                        }`}
                        min={minDays}
                        required
                      />
                      {isInvalid && (
                        <p className="text-xs text-red-600 mt-1">
                          Minimum: {defaultDays} nap (gyógyszer karencia). Nem csökkenthető ez alá.
                        </p>
                      )}
                      {defaultDays > 0 && !isInvalid && (
                        <p className="text-xs text-gray-500 mt-1">
                          Minimum: {defaultDays} nap (gyógyszer karencia)
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* 8. Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('notes', 'treatments') || 'Megjegyzés'}
                </label>
                <textarea
                  value={formState.notes}
                  onChange={(e) => setFormState(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                  placeholder={t('optional_notes_placeholder', 'treatments') || 'Opcionális megjegyzések...'}
                />
              </div>

              {/* 9. Veterinary Approved */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="veterinary_approved"
                  checked={formState.veterinary_approved}
                  onChange={(e) => setFormState(prev => ({ ...prev, veterinary_approved: e.target.checked }))}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="veterinary_approved" className="ml-2 block text-sm text-gray-700">
                  {t('veterinary_approved', 'treatments') || 'Állatorvos által jóváhagyva'}
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={submitting}
              >
                {t('cancel', 'action')}
              </button>
              <button
                onClick={handleAddTreatment}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting || !stockSufficient}
              >
                {submitting ? 'Mentés...' : t('save', 'action')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animal Selector Modal */}
      <AnimalSelectorModal
        isOpen={showAnimalSelector}
        onClose={() => setShowAnimalSelector(false)}
        onSelect={handleAnimalSelected}
        mode="single"
        title={t('select_animal', 'treatments') || 'Select animal'}
      />
    </div>
  );
};

export default Treatments;
